/* Developer : Nithin
Date : 14-07-2026
changes: Commented out the Materials Tab as it is not required */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
   UserCircle,
   CreditCard,
   Users2,
   Package,
   ChevronDown,
   ChevronUp,
   Plus,
   CheckSquare,
   FileText,
   Upload,
   Loader2,
   Check,
   Receipt,
   CheckCircle,
   Download,
   Eye,
   Images,
   Trash2,
   PenTool,
   ClipboardCheck
} from 'lucide-react';
import { ProjectManagerAssignment, ProjectManagerAssignmentForm } from '../../verve-projects-flow-final/components/ProjectManagerAssignment';
import { CreditPaymentForm } from '../../verve-projects-flow-final/components/project_payment';
import { OverviewActivityTables } from '../../verve-projects-flow-final/components/OverviewActivityTables';
import { LeadExecutionTeamAssignmentForm } from './LeadExecutionTeamAssignmentForm';
import { fmt, fmtDate } from "@/lib/formatters";
import { ViewReceivedAmountModal } from '../../verve-accounts/components/ViewReceivedAmountModal';
import { ProjectInventoryTab } from '../../verve-projects-flow-final/components/ProjectInventoryTab';
import { LeadExecutionMaterialsTab } from './LeadExecutionMaterialsTab';
import { ProjectTasksTab } from '../../verve-projects-flow-final/components/ProjectTasksTab';
import { useProjectMaterialRequests } from '../../verve-projects-flow-final/hooks/useProjectMaterialRequests';
import DesignsTab from './designs';
import { SignOffCopyTab } from './sign_off_copy';
import DocumentsTab from './documents';
import GalleryTab from './gallery';
import BudgetTab from './budget';
import OrderingTab from './ordering';
import FinalReviewTab from './final_review';
import Toast from "@/components/ui/Toast";
import Modal from "@/components/ui/Modal";
import { DataTable } from '@/components/ui/DataTable';
import { useOverviewTables } from '../../verve-projects-flow-final/hooks/useOverviewTables';
import { TEAM_COLUMNS, TASK_COLUMNS, TRANSACTION_COLUMNS, EXPENSE_COLUMNS, DOCUMENT_COLUMNS } from '../../verve-projects-flow-final/constants/overviewTableConstants';
import { ScheduleTaskModal } from '../../verve-projects-flow-final/modals/ScheduleTaskModal';
import { AddWorkUpdateModal } from '../../verve-projects-flow-final/modals/AddWorkUpdateModal';
import { LeadExecutionTradeWorkUpdateModal } from '../modals/LeadExecutionTradeWorkUpdateModal';
import { LeadExecutionTeamTasksViewModal } from './team_tasks_view';
import { TaskDetailsModal } from '../../verve-projects-flow-final/modals/TaskDetailsModal';
import { GalleryView } from '../../verve-projects-flow-final/components/gallery_view';
import { ProjectMediaUploadModal } from '../../verve-projects-flow-final/components/ProjectMediaUploadModal';
import { hasProjectInstallationTeam, hasMaterialReceiptForTaskAssignment, fieldOfficersEligibleForTasks } from '../../verve-projects-flow-final/utils/projectTeamAccess';
import { PROJECT_PIPELINE_STAGES } from '../../verve-projects-flow-final/constants/projectConstants';
import { getActivePipelineIndex } from '../../verve-projects-flow-final/constants/projectPipelineStages';
import api, { BASE_URL } from "@/lib/API";
import { projectService } from '../../verve-projects-flow-final/services/projectService';
import { budgetQuotationService } from '../../budget/services/budgetQuotationService';
import { orderingQuotationService } from '../../ordering/services/orderingQuotationService';

const apiFileBase = () => String(BASE_URL).replace(/\/api\/?$/, "");

// Helper Components for Accordion
const AccordionSection = ({ title, icon, children, isOpen, onToggle, isCompleted, action, statusLabel, statusColor, subtext }) => {
   return (
      <div className={`overflow-hidden shadow-sm transition-all duration-300 ${isOpen ? 'border-b border-[#f97316]' : ''}`}>
         <div className={`flex items-center justify-between hover:bg-slate-50 transition-colors ${isOpen ? 'bg-slate-50/50 border-b border-slate-100' : ''}`}>
            <button
               onClick={onToggle}
               className="flex-1 flex items-center justify-between px-4 py-3 cursor-pointer"
            >
               <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shadow-sm border ${isCompleted ? 'bg-emerald-50 border-emerald-100 text-emerald-500' : 'bg-white border-slate-100 text-slate-500'
                     }`}>
                     {icon}
                  </div>
                  <div className="flex flex-col items-start text-left">
                     <span className={`text-[14px] font-semibold tracking-tight ${isCompleted ? 'text-emerald-700' : 'text-slate-700'
                        }`}>{title}</span>
                     {subtext && <span className="text-[11px] font-medium text-amber-600 mt-0.5">{subtext}</span>}
                  </div>
               </div>
            </button>
            <div className="flex items-center gap-4 pr-4">
               {action}
               <div onClick={onToggle} className="cursor-pointer">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
               </div>
            </div>
         </div>
         {isOpen && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
               {children}
            </div>
         )}
      </div>
   );
};

export const LeadExecutionOverviewTab = ({ project, onUpdate, setActiveTab, tasksInventoryLocked = false }) => {
   const [openSections, setOpenSections] = useState({
      finalDesign: false,
      budget: false,
      ordering: false,
      signOff: false,
      payments: false,
      team: false,
      materials: false,
      documentsAndDesigns: false,
      gallery: false
   });
   const [toast, setToast] = useState(null);
   const [activeModal, setActiveModal] = useState(null); // 'pm', 'payment', 'team', 'task', 'upload', 'taskDetail'
   const [editingTeamMember, setEditingTeamMember] = useState(null);
   const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
   const [expenseInitialData, setExpenseInitialData] = useState(null);
   const [selectedTask, setSelectedTask] = useState(null);
   const [docModalState, setDocModalState] = useState({ hasFiles: false, submitting: false, canUpload: false });
   const [documentToApprove, setDocumentToApprove] = useState(null);
   const docFormRef = useRef();
   const [workUpdateTask, setWorkUpdateTask] = useState(null);
   const [updateTrade, setUpdateTrade] = useState(null);
   const [historyTrade, setHistoryTrade] = useState(null);
   const { transactionRows, formatRowDate, formatRowDateTime, tasks, refreshTasks } = useOverviewTables(project);
   const { requests, allDelivered } = useProjectMaterialRequests(project?._id);
   const [isApproving, setIsApproving] = useState(false);
   const [txTab, setTxTab] = useState('credits'); // 'credits' | 'expenses'
   const [receivedAmounts, setReceivedAmounts] = useState([]);
   const [viewReceipt, setViewReceipt] = useState(null);

   const activeStageIndex = useMemo(() => {
      const stages = ['Design', 'Budget', 'Ordering', 'Sign Off copy', 'Payments'];
      const current = String(project?.status || '').trim().toLowerCase();
      const idx = stages.findIndex(s => s.toLowerCase() === current);
      return idx !== -1 ? idx : 0;
   }, [project?.status]);

   const [hasApprovedDesign, setHasApprovedDesign] = useState(false);
   const [hasApprovedBudget, setHasApprovedBudget] = useState(false);
   const [hasApprovedOrdering, setHasApprovedOrdering] = useState(false);

   useEffect(() => {
      if (!project?._id) return;
      const leadIdStr = String(project.leadId?._id || project.leadId || project._id || '');
      const customIdStr = String(project.leadId?.customId || '');

      api.get(`/lead-executions-designs/${project._id}/designs`)
         .then(res => {
            const list = res?.data?.data || [];
            setHasApprovedDesign(list.some(d => d.isApproved || String(d.status).toLowerCase() === 'approved'));
         })
         .catch(() => { });

      budgetQuotationService.getQuotations()
         .then(res => {
            const list = res?.data || [];
            const match = list.filter(q => {
               const qLid = String(q.contactRef || q.contactObj?._id || q.leadId?._id || q.leadId || q.clientId?._id || q.clientId || '');
               const qCid = String(q.customId || q.contactRef || '');
               return (leadIdStr && qLid === leadIdStr) || (customIdStr && qCid === customIdStr);
            });
            setHasApprovedBudget(match.some(q => ['approved', 'submitted'].includes(String(q.status || '').toLowerCase())));
         })
         .catch(() => { });

      orderingQuotationService.getQuotations()
         .then(res => {
            const list = res?.data || [];
            const match = list.filter(q => {
               const qLid = String(q.contactRef || q.contactObj?._id || q.leadId?._id || q.leadId || q.clientId?._id || q.clientId || '');
               const qCid = String(q.customId || q.contactRef || '');
               return (leadIdStr && qLid === leadIdStr) || (customIdStr && qCid === customIdStr);
            });
            setHasApprovedOrdering(match.some(q =>
               ['approved', 'payment completed', 'paid', 'completed'].includes(String(q.status || '').toLowerCase()) ||
               ['order placed', 'placed', 'processing', 'dispatched', 'delivered'].includes(String(q.orderStatus || '').toLowerCase())
            ));
         })
         .catch(() => { });
   }, [project?._id, project?.status]);

   const isDesignCompleted = activeStageIndex > 0 || hasApprovedDesign;
   const isBudgetCompleted = activeStageIndex > 1 || hasApprovedBudget;
   const isOrderingCompleted = activeStageIndex > 2 || hasApprovedOrdering;
   const isSignOffUnlocked = activeStageIndex >= 3 || (isDesignCompleted && isBudgetCompleted && isOrderingCompleted);

   useEffect(() => {
      if (openSections['payments'] && project?._id) {
         api.get('/received-amounts')
            .then(res => {
               const rawAmounts = res?.data?.data || [];
               const projectAmounts = rawAmounts.filter(item =>
                  item.relatedProjectId?._id === project._id ||
                  item.relatedProjectId === project._id ||
                  item.projectId === project._id ||
                  item.relatedProjectId?.customId === project.projectId ||
                  item.relatedProjectId?._id === project.leadId?._id ||
                  item.relatedProjectId === project.leadId?._id
               );
               const formatted = projectAmounts.map(item => ({
                  _id: item._id,
                  receiptId: item.receiptId,
                  projectId: item.relatedProjectId?.customerCustomId || item.relatedProjectId?.projectId || item.relatedProjectId?.customId || "—",
                  clientName: item.relatedProjectId?.customerName || item.relatedProjectId?.firstName || "—",
                  projectName: item.relatedProjectId?.projectName || "—",
                  project: item.relatedProjectId,
                  paymentStage: item.paymentStage,
                  paymentDate: item.paymentDate,
                  paymentMode: item.paymentMethod,
                  amountReceived: item.amount,
                  balanceAmount: 0,
                  status: item.status,
                  transactionId: item.transactionId,
                  bankName: item.bankName,
                  branch: item.branch,
                  upiPhone: item.upiPhone,
                  receiverName: item.receiverName,
                  receiverPhone: item.receiverPhone,
                  chequeNumber: item.chequeNumber,
                  chequeDate: item.chequeDate,
                  remarks: item.remarks,
                  isFollowup: item.isFollowup,
                  followupReason: item.followupReason,
                  approxPaymentDate: item.approxPaymentDate,
                  followupDescription: item.followupDescription,
                  isBlinkingStopped: item.isBlinkingStopped
               }));
               setReceivedAmounts(formatted);
            })
            .catch(err => console.error("Failed to fetch received amounts:", err));
      }
   }, [openSections, project?._id]);

   const currentUser = useMemo(() => {
      try {
         const userStr = localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user');
         return userStr ? JSON.parse(userStr) : {};
      } catch (err) {
         return {};
      }
   }, []);

   const myTask = useMemo(() => {
      const uid = currentUser._id || currentUser.id;
      const isFO = String(currentUser.role || '').toLowerCase().includes('field');

      if (!tasks || !Array.isArray(tasks) || tasks.length === 0) return null;

      let task = tasks.find(t => {
         const aid = t.assignedOfficer?._id || t.assignedOfficer?.id || (typeof t.assignedOfficer === 'string' ? t.assignedOfficer : null);
         return aid && uid && String(aid) === String(uid) && t.status !== 'COMPLETED';
      });

      if (!task && isFO) {
         task = tasks.find(t => t.status !== 'COMPLETED');
      }

      return task;
   }, [tasks, currentUser]);

   const toggleSection = (section) => {
      const isOpenning = !openSections[section];
      if (section === 'signOff' && isOpenning && !isSignOffUnlocked) {
         if (setToast) setToast({ message: "Complete before steps to unlock Sign Off copy.", type: "error" });
         return;
      }
      setOpenSections(prev => {
         const isOpen = !prev[section];
         if (isOpen) {
            const stageMap = {
               finalDesign: 'Design',
               budget: 'Budget',
               ordering: 'Ordering',
               signOff: 'Sign Off copy',
               payments: 'Payments',
               team: 'Team Assignment',
               materials: 'Material Requested',
               documents: 'Documents'
            };
            const targetStage = stageMap[section];
            if (targetStage && project?._id) {
               api.put(`/lead-executions/${project._id}/stage`, { status: targetStage })
                  .then(({ data }) => {
                     if (data?.success && onUpdate) onUpdate(true); // triggers silent fetch
                  })
                  .catch(console.error);
            }
         }
         return {
            ...prev,
            [section]: isOpen
         };
      });
   };

   const formatCurrency = (amount) => {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-IN');
   };

   const getInitials = (name) => {
      if (!name) return 'U';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
   };

   const handleApproveStage = async () => {
      setIsApproving(true);
      try {
         const data = await api.post(`/verve-projects/${project._id}/documents/approve-stage`);
         if (data) {
            onUpdate(data);
            setToast({ message: 'Documents stage approved successfully', type: 'success' });
         }
      } catch (err) {
         setToast({ message: err.response?.data?.message || err.message || 'Failed to approve documents stage', type: 'error' });
      } finally {
         setIsApproving(false);
      }
   };

   const isPmAssigned = !!project?.projectManager && (!Array.isArray(project.projectManager) || project.projectManager.length > 0);
   const isAdvancePaid = (project?.paymentSummary?.totalPaid || 0) > 0 || project?.advancePaymentStatus === 'Approved';
   const isTeamAssigned = (project?.fieldOfficers?.length || 0) > 0;
   const isTasksAssigned =
      (isTeamAssigned &&
         project.fieldOfficers.every(officer => {
            const officerId = typeof officer === 'object' ? (officer._id || officer.id) : officer;
            return (tasks || []).some(t => {
               const taskOfficerId = typeof t.assignedOfficer === 'object' ? (t.assignedOfficer?._id || t.assignedOfficer?.id) : t.assignedOfficer;
               return String(taskOfficerId) === String(officerId);
            });
         }) &&
         (tasks || []).length > 0 &&
         tasks.every(t => t.status === 'COMPLETED')) ||
      ['balance update', 'documents', 'completed'].includes(String(project?.status || '').toLowerCase());

   const isDocumentsApproved = !!project?.documentsStageComplete;
   const activePipelineIndex = useMemo(() => {
      return getActivePipelineIndex(project?.projectStatus || project?.status, project?.installationMilestones || {}, {
         documentsStageComplete: !!project?.documentsStageComplete,
         teamAssigned: (project?.fieldOfficers?.length || 0) > 0,
         pmAssigned: !!project?.projectManager && (!Array.isArray(project.projectManager) || project.projectManager.length > 0)
      });
   }, [project]);
   const isInDocumentStage = PROJECT_PIPELINE_STAGES[activePipelineIndex] === 'Documents';

   const hasDocumentsUploaded = (project?.mergedDocuments?.length || 0) > 0 || (project?.documentUploads?.length || 0) > 0 || (project?.documents?.length || 0) > 0;
   const eligibleFieldOfficers = useMemo(() => fieldOfficersEligibleForTasks(project, requests), [project, requests]);
   const canAssignTask = hasProjectInstallationTeam(project) && eligibleFieldOfficers.length > 0 && !tasksInventoryLocked;
   const canUploadDocs = ['owner', 'admin', 'designer'].includes(String(currentUser?.role || '').toLowerCase());
   const allOfficersReceived = useMemo(() => {
      if (!project?.fieldOfficers || project.fieldOfficers.length === 0) return false;
      return project.fieldOfficers.every(officer => {
         const offId = typeof officer === 'object' ? (officer._id || officer.id) : officer;
         return (project.materialReceipts || []).some(r => String(r.confirmedBy?._id || r.confirmedBy) === String(offId));
      });
   }, [project?.fieldOfficers, project?.materialReceipts]);

   const hasPendingMaterial = (requests || []).length > 0 && !allDelivered;
   const isMaterialRequestComplete = allOfficersReceived || allDelivered || (requests || []).some(r => ['Site Received', 'Delivered', 'Received', 'Completed'].includes(r.status)) || String(project?.status || '').toLowerCase() === 'completed';


   const handleExportPaymentTransactions = () => {
      const allTxs = project?.paymentSummary?.transactions || [];
      // Only export transactions that are fully paid or are credits (which are inherently received payments)
      const txs = allTxs.filter(t => t.kind === 'credit' || t.requestStatus === 'paid');

      const headers = [
         'Client Name', 'Project ID', 'Customer Project ID', 'Customer', 'Site Name', 'PO Status', 'Location', 'PO Value', 'Budget',
         'Transaction ID', 'Date', 'Type', 'Category', 'Role Type', 'EP Type',
         'Description/Reason', 'Requested By', 'Requested Amount', 'Approved Amount',
         'Disbursed Amount', 'Status', 'Payment Method', 'Ref ID', 'Bank Name',
         'Cheque Number', 'UPI/Phone', 'Receiver Name', 'Internal Notes'
      ];

      const escapeCsv = (val) => {
         if (val === null || val === undefined) return '""';
         if (typeof val === 'number') return val;
         const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
         return `"${str}"`;
      };

      const rows = txs.map(t => [
         escapeCsv(project?.customerName || ''),
         escapeCsv(project?.projectId || ''),
         escapeCsv(project?.applicationId || ''),
         escapeCsv(project?.projectName || ''),
         escapeCsv(project?.siteName || ''),
         escapeCsv(project?.status || ''),
         escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
         escapeCsv(project?.projectValue || 0),
         escapeCsv(project?.targetCost || 0),
         escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
         escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
         escapeCsv(t.kind === 'credit' ? 'CREDIT' : 'EXPENSE'),
         escapeCsv(t.categoryLabel || t.expenseCategory || t.paymentType || ''),
         escapeCsv(t.roleType || ''),
         escapeCsv(t.epType || ''),
         escapeCsv(t.expenseReason || t.internalNotes || ''),
         escapeCsv(t.requestedBy?.name || t.createdBy?.name || ''),
         escapeCsv(t.amount || 0),
         escapeCsv(t.approvedAmount || 0),
         escapeCsv(t.disbursedAmount || 0),
         escapeCsv(t.requestStatus || 'paid'),
         escapeCsv(t.paymentMethod || ''),
         escapeCsv(t.refId || ''),
         escapeCsv(t.bankName || ''),
         escapeCsv(t.chequeNumber || ''),
         escapeCsv(t.upiPhone || t.receiverPhone || ''),
         escapeCsv(t.receiverName || t.recipient || ''),
         escapeCsv(t.internalNotes || t.approvalNote || '')
      ].join(','));

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${project?.projectId || 'Project'}_payment_transactions.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
   };

   const progressData = useMemo(() => {
      const latestUpdatesByTrade = (project?.activityLog || [])
         .filter(log => log.type === 'work_update')
         .map(update => {
            const match = update.message?.match(/Site update \((.*?)\):\s+(.*?)(?:\s+—\s*(.*))?$/i);
            const tradeVal = (update.trade || (match ? match[1] : 'unknown')).toLowerCase();
            return {
               trade: tradeVal,
               originalTrade: update.trade ? (update.trade.toUpperCase()) : (match ? match[1].toUpperCase() : 'UNKNOWN'),
               status: update.status || (match ? match[2] : 'Unknown'),
               description: update.detail || update.description || (match ? match[3] : '-'),
               actorName: update.actorName,
               actorRole: update.actorRole,
               progress: update.progress,
               workStartTime: update.workStartTime || '',
               workEndTime: update.workEndTime || '',
               siteName: project?.siteName || project?.customerName || '-',
               at: update.at,
               images: update.images || []
            };
         })
         .filter(update => update.trade !== 'unknown')
         .sort((a, b) => new Date(a.at) - new Date(b.at))
         .reduce((acc, curr) => {
            acc[curr.trade] = curr;
            return acc;
         }, {});

      const activeAssignedOfficers = (project?.fieldOfficers || []).filter(fo => typeof fo === 'object' && Boolean(fo.installationTrade || fo.role));
      const uniqueAssignedTrades = activeAssignedOfficers.map(fo => (fo.installationTrade || fo.role).toLowerCase());

      const allActiveTrades = Array.from(new Set([
         ...uniqueAssignedTrades
         /* 
         ...Object.keys(latestUpdatesByTrade),
         ...Object.keys(project?.installationMilestones || {})
            .map(k => k.replace('Progress', '').toLowerCase())
            .filter(Boolean)
         */
      ].filter(Boolean)));

      if (allActiveTrades.length === 0) return [];

      return allActiveTrades.map(tradeValue => {
         const assignedOfficers = activeAssignedOfficers.filter(fo => (fo.installationTrade || fo.role)?.toLowerCase() === tradeValue);
         const assignedName = assignedOfficers.map(fo => fo.name).join(', ') || '-';
         const log = latestUpdatesByTrade[tradeValue];

         if (log) {
            return {
               ...log,
               trade: tradeValue.toUpperCase(),
               assignedName: assignedName,
               assignedOfficers: assignedOfficers,
               requestId: tradeValue
            };
         }

         return {
            trade: tradeValue.toUpperCase(),
            assignedName: assignedName,
            assignedOfficers: assignedOfficers,
            status: 'Not Started',
            description: '-',
            progress: 0,
            siteName: project?.siteName || project?.customerName || '-',
            at: null,
            requestId: tradeValue
         };
      });
   }, [project]);

   return (
      <div className="animate-in slide-in-from-bottom duration-500 w-full flex flex-col gap-4 pb-12">
         {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

         {/* Accordion Container */}
         <div className="flex flex-col gap-2">


            {/* 1. Final Design */}
            <AccordionSection
               title="Project Designs / Drawings"
               icon={<PenTool className="w-4 h-4" />}
               isOpen={openSections['finalDesign']}
               onToggle={() => toggleSection('finalDesign')}
               isCompleted={isDesignCompleted}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <DesignsTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

            {/* 2. Budget */}
            <AccordionSection
               title="Budget / Final Quotation"
               icon={<FileText className="w-4 h-4" />}
               isOpen={openSections['budget']}
               onToggle={() => toggleSection('budget')}
               isCompleted={isBudgetCompleted}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <BudgetTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

            {/* 3. Ordering */}
            <AccordionSection
               title="Order Appliances / Materials"
               icon={<Package className="w-4 h-4" />}
               isOpen={openSections['ordering']}
               onToggle={() => toggleSection('ordering')}
               isCompleted={isOrderingCompleted}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <OrderingTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

            {/* 4. Sign Off copy */}
            <AccordionSection
               title="Sign Off copy"
               icon={<CheckSquare className="w-4 h-4" />}
               isOpen={openSections['signOff']}
               onToggle={() => toggleSection('signOff')}
               isCompleted={activeStageIndex >= 3 || openSections['signOff']}
               subtext={!isSignOffUnlocked ? "⚠️ Complete Design, Budget & Ordering stages before unlocking" : null}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <SignOffCopyTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

            {/* 5. Payments */}
            <AccordionSection
               title="Payments & Transactions"
               icon={<CreditCard className="w-4 h-4" />}
               isOpen={openSections['payments']}
               onToggle={() => { toggleSection('payments'); setTxTab('credits'); }}
               isCompleted={isAdvancePaid}
            >
               <div className="flex flex-col">
                  {/* Top bar: tabs + action buttons */}
                  <div className="flex items-center justify-between px-4 pt-3 pb-0 bg-white border-b border-slate-100">
                     {/* Tabs */}
                     <div className="flex gap-0">
                        <button
                           onClick={() => setTxTab('credits')}
                           className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 transition-all ${txTab === 'credits'
                              ? 'border-emerald-500 text-emerald-600'
                              : 'border-transparent text-slate-400 hover:text-slate-600'
                              }`}
                        >
                           <CreditCard className="w-3.5 h-3.5" />
                           Credits
                           <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${txTab === 'credits' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                              }`}>
                              {receivedAmounts.length}
                           </span>
                        </button>
                        <button
                           onClick={() => setTxTab('expenses')}
                           className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 transition-all ${txTab === 'expenses'
                              ? 'border-rose-500 text-rose-600'
                              : 'border-transparent text-slate-400 hover:text-slate-600'
                              }`}
                        >
                           <Receipt className="w-3.5 h-3.5" />
                           Expenses
                           <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${txTab === 'expenses' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'
                              }`}>
                              {transactionRows.filter(r => r.typeLabel === 'Expense' || r.typeLabel === 'Expense (pending)').length}
                           </span>
                        </button>
                     </div>
                     {/* Action buttons */}
                     <div className="flex gap-2 pb-2">
                        <button
                           onClick={handleExportPaymentTransactions}
                           className="flex items-center cursor-pointer gap-2 px-3 py-1.5 border border-emerald-600 bg-transparent text-emerald-600 hover:bg-emerald-50 rounded-md text-[11px] font-semibold shadow-sm"
                        >
                           <Download className="w-3.5 h-3.5" />
                           Export All
                        </button>
                        <button
                           onClick={() => setActiveModal('payment')}
                           className="flex items-center cursor-pointer gap-2 px-3 py-1.5 border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50 rounded-md text-[11px] font-semibold shadow-sm"
                        >
                           <Plus className="w-3.5 h-3.5" />
                           Record Payment
                        </button>
                     </div>
                  </div>

                  {/* Credits tab */}
                  {txTab === 'credits' && (() => {
                     const rows = receivedAmounts;
                     const total = rows.reduce((s, r) => s + (r.amountReceived || 0), 0);

                     const CREDITS_COLUMNS = [
                        {
                           header: "Payment Stage",
                           key: "paymentStage",
                           render: (row) => (
                              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-indigo-700 bg-indigo-50 border border-indigo-100 whitespace-nowrap">
                                 {row.paymentStage || "—"}
                              </span>
                           )
                        },
                        {
                           header: "Payment Date",
                           key: "paymentDate",
                           render: (row) => (
                              <div className="text-slate-600 !text-[10px] flex items-center whitespace-nowrap">
                                 {row.paymentDate ? fmtDate(row.paymentDate) : "—"}
                              </div>
                           )
                        },
                        {
                           header: "Reason (Follow-up)",
                           key: "reason",
                           render: (row) => {
                              const text = row.isFollowup ? row.followupReason || "—" : "—";
                              return (
                                 <div className="text-slate-600 !text-[10px] whitespace-nowrap truncate max-w-[120px]" title={text}>
                                    {text}
                                 </div>
                              );
                           }
                        },
                        {
                           header: "Approx Date",
                           key: "approxDate",
                           render: (row) => (
                              <div className="text-slate-600 !text-[10px] whitespace-nowrap">
                                 {row.isFollowup && row.approxPaymentDate ? fmtDate(row.approxPaymentDate) : "—"}
                              </div>
                           )
                        },
                        {
                           header: "Payment Mode",
                           key: "paymentMode",
                           render: (row) => (
                              <div className="text-slate-500 !text-[10px] font-medium flex items-center capitalize whitespace-nowrap">
                                 {row.paymentMode?.replace(/_/g, " ") || "—"}
                              </div>
                           )
                        },
                        {
                           header: "Amount Received",
                           key: "amountReceived",
                           render: (row) => (
                              <div className="text-emerald-500 !text-[10px] font-bold flex items-center whitespace-nowrap">
                                 {fmt(row.amountReceived || 0)}
                              </div>
                           )
                        },
                        {
                           header: "Balance Amount",
                           key: "balanceAmount",
                           render: (row) => (
                              <div className="text-rose-500 !text-[10px] font-bold flex items-center whitespace-nowrap">
                                 {fmt(row.balanceAmount || 0)}
                              </div>
                           )
                        },
                        {
                           header: "Actions",
                           key: "actions",
                           render: (row) => (
                              <div className="flex items-center justify-center whitespace-nowrap">
                                 <button
                                    onClick={(e) => { e.stopPropagation(); setViewReceipt(row); }}
                                    className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                                    title="View Detail"
                                 >
                                    <Eye size={16} />
                                 </button>
                              </div>
                           )
                        }
                     ];

                     return (
                        <div className="animate-in fade-in duration-200">
                           <DataTable
                              columns={CREDITS_COLUMNS}
                              data={rows}
                              emptyMessage="No credit transactions recorded yet."
                           />
                           {rows.length > 0 && (
                              <div className="flex justify-end items-center gap-2 px-4 py-2.5 bg-emerald-50 border-t border-emerald-100">
                                 <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">Total Received</span>
                                 <span className="text-[12px] font-black text-emerald-700">₹{formatCurrency(total)}</span>
                              </div>
                           )}

                           <ViewReceivedAmountModal
                              isOpen={!!viewReceipt}
                              onClose={() => setViewReceipt(null)}
                              receipt={viewReceipt}
                           />
                        </div>
                     );
                  })()}

                  {/* Expenses tab */}
                  {txTab === 'expenses' && (() => {
                     const rows = transactionRows.filter(r => r.typeLabel === 'Expense' || r.typeLabel === 'Expense (pending)');
                     const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
                     return (
                        <div className="animate-in fade-in duration-200">
                           <DataTable
                              columns={EXPENSE_COLUMNS(formatCurrency, formatRowDateTime)}
                              data={rows}
                              emptyMessage="No expenses recorded yet."
                           />
                           {rows.length > 0 && (
                              <div className="flex justify-end items-center gap-2 px-4 py-2.5 bg-rose-50 border-t border-rose-100">
                                 <span className="text-[11px] font-bold text-rose-700 uppercase tracking-widest">Total Expenses</span>
                                 <span className="text-[12px] font-black text-rose-700">₹{formatCurrency(total)}</span>
                              </div>
                           )}
                        </div>
                     );
                  })()}
               </div>
            </AccordionSection>

            {/* 6. Team Tasks */}
            <AccordionSection
               title="Installation Team & Work Progress"
               icon={<Users2 className="w-4 h-4" />}
               isOpen={openSections['team']}
               onToggle={() => toggleSection('team')}
               isCompleted={isTeamAssigned}
            >
               <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden mt-2 shadow-sm">
                  <div className="p-3 border-b border-slate-200 flex justify-end gap-2 bg-slate-50">
                     {/* Commented out per user request: "comment that unwanted text"
                      <div>
                         <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Users2 className="w-4 h-4 text-[#2b448f]" /> Team Assignments & Work Progress
                         </h4>
                         <p className="text-[11px] text-slate-500 mt-0.5">Manage team members and track trade-wise site completion</p>
                      </div>
                      */}
                     <div className="flex items-center gap-2">
                        <button
                           type="button"
                           onClick={() => { setEditingTeamMember(null); setActiveModal('team'); }}
                           disabled={tasksInventoryLocked}
                           className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[11px] font-bold tracking-wide transition-all shadow-sm ${tasksInventoryLocked ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-white border border-[#2b448f] text-[#2b448f] hover:bg-blue-50 cursor-pointer'
                              }`}
                        >
                           <Plus className="w-3.5 h-3.5" /> Assign Member
                        </button>
                        <button
                           type="button"
                           onClick={() => {
                              const firstTrade = progressData.length > 0 ? progressData[0].trade : 'GENERAL';
                              setUpdateTrade(firstTrade.toLowerCase());
                           }}
                           className="flex items-center gap-1.5 px-3.5 py-2 border border-[#2b448f] bg-[#2b448f] text-white hover:bg-[#1a2d5f] rounded-md text-[11px] font-bold tracking-wide transition-colors shadow-sm cursor-pointer"
                        >
                           <Plus className="w-3.5 h-3.5" /> Work Update
                        </button>
                     </div>
                  </div>
                  <DataTable
                     columns={[
                        {
                           header: 'Role Name',
                           key: 'trade',
                           minWidth: '130px',
                           render: (row) => (
                              <span className="text-[11px] font-bold uppercase tracking-wider text-[#2b448f] bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
                                 {row.trade || '-'}
                              </span>
                           )
                        },
                        {
                           header: 'Assigned Member(s)',
                           key: 'assignedOfficers',
                           minWidth: '220px',
                           render: (row) => {
                              const officers = row.assignedOfficers || [];
                              if (officers.length === 0) {
                                 return (
                                    <div className="flex items-center gap-2">
                                       <span className="text-[12px] text-slate-400 italic">Unassigned</span>
                                       <button
                                          type="button"
                                          onClick={() => { setEditingTeamMember(null); setActiveModal('team'); }}
                                          disabled={tasksInventoryLocked}
                                          className="text-[11px] text-[#2b448f] font-semibold underline hover:text-blue-700 cursor-pointer"
                                       >
                                          + Assign
                                       </button>
                                    </div>
                                 );
                              }
                              return (
                                 <div className="flex flex-col gap-1 py-0.5">
                                    {officers.map((fo, idx) => (
                                       <div key={fo._id || fo.id || idx} className="flex items-center justify-between gap-2 group">
                                          <div className="flex flex-col min-w-0">
                                             <span className="text-[12px] font-semibold text-slate-700 truncate">{fo.name}</span>
                                             {fo.phone && <span className="text-[10px] text-slate-500">{fo.phone}</span>}
                                          </div>
                                          {!tasksInventoryLocked && (
                                             <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                   type="button"
                                                   title="Remove Member from Project"
                                                   onClick={async () => {
                                                      const currentIds = (project.fieldOfficers || []).map(m => typeof m === 'object' ? m._id || m.id : m);
                                                      const newIds = currentIds.filter(id => String(id) !== String(fo._id || fo.id));
                                                      await projectService.updateProject(project._id, { fieldOfficers: newIds });
                                                      if (onUpdate) onUpdate(await projectService.getProjectById(project._id));
                                                   }}
                                                   className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                                                >
                                                   <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                             </div>
                                          )}
                                       </div>
                                    ))}
                                 </div>
                              );
                           }
                        },
                        /* Commented out per user request: "comment site name also"
                        {
                           header: 'Site Name',
                           key: 'siteName',
                           minWidth: '150px',
                           render: (row) => (
                              <span className="text-[12px] font-medium text-slate-800 uppercase tracking-tight">{row.siteName || '-'}</span>
                           )
                        },
                        */
                        {
                           header: 'Work Status',
                           key: 'status',
                           minWidth: '140px',
                           render: (row) => {
                              const sStatus = row.status || '-';
                              let colorClass = 'bg-slate-50 text-slate-600 border-slate-200';
                              if (String(sStatus).toLowerCase() === 'in progress' || String(sStatus).toLowerCase() === 'started') colorClass = 'bg-blue-50 text-blue-700 border-blue-200';
                              if (String(sStatus).toLowerCase() === 'on hold' || String(sStatus).toLowerCase() === 'waiting for material') colorClass = 'bg-rose-50 text-rose-700 border-rose-200';
                              if (String(sStatus).toLowerCase() === 'completed') colorClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';

                              return (
                                 <span className={`px-2 py-0.5 rounded text-[11px] font-semibold tracking-tight border ${colorClass}`}>
                                    {sStatus}
                                 </span>
                              );
                           }
                        },
                        {
                           header: 'Last Updated',
                           key: 'workEndTime',
                           minWidth: '150px',
                           render: (row) => {
                              const val = row.workEndTime || row.workStartTime || row.at;
                              if (!val) return <span className="text-slate-400 text-[12px]">-</span>;
                              try {
                                 const d = new Date(val);
                                 return isNaN(d.getTime()) ? (
                                    <span className="text-[12px] text-slate-600">{val}</span>
                                 ) : (
                                    <span className="text-[12px] text-slate-600 whitespace-nowrap font-medium">
                                       {d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                 );
                              } catch (e) { return <span className="text-[12px] text-slate-600">{val}</span>; }
                           }
                        },
                        {
                           header: 'Notes',
                           key: 'notes',
                           minWidth: '180px',
                           render: (row) => (
                              <span className="text-[12px] font-medium text-slate-600 line-clamp-1 max-w-[220px]" title={row.description}>
                                 {row.description || '-'}
                              </span>
                           )
                        },
                        {
                           header: 'Progress',
                           key: 'progress',
                           minWidth: '140px',
                           render: (row) => (
                              <div className="flex items-center gap-3">
                                 <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                    <div
                                       className={`h-full ${row.progress === 100 ? 'bg-emerald-500' : 'bg-[#2b448f]'}`}
                                       style={{ width: `${row.progress || 0}%` }}
                                    />
                                 </div>
                                 <span className="text-[11px] font-bold text-slate-700">{row.progress || 0}%</span>
                              </div>
                           )
                        },
                        {
                           header: 'Actions',
                           key: 'action',
                           minWidth: '100px',
                           render: (row) => (
                              <div className="flex items-center gap-1">
                                 {/* Commented out per user request: "comment the + button ok not required ok"
                                 <button 
                                    onClick={() => setUpdateTrade(row.trade.toLowerCase())}
                                    className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                                    title="Add Work Update"
                                 >
                                    <Plus className="w-4 h-4" />
                                 </button>
                                 */}
                                 <button
                                    onClick={() => setHistoryTrade(row.trade)}
                                    className="p-1.5 rounded-md text-slate-400 hover:text-[#2b448f] hover:bg-slate-100 transition-colors cursor-pointer"
                                    title="View Update History"
                                 >
                                    <Eye className="w-4 h-4" />
                                 </button>
                              </div>
                           )
                        }
                     ]}
                     data={progressData}
                     wrapperClassName="overflow-x-auto w-full max-w-full"
                  />
               </div>

               {/* Trade History Modal */}
               <LeadExecutionTeamTasksViewModal
                  isOpen={!!historyTrade}
                  onClose={() => setHistoryTrade(null)}
                  trade={historyTrade}
                  updates={(project?.activityLog || [])
                     .filter(log => log.type === 'work_update')
                     .map(update => {
                        const match = update.message?.match(/Site update \((.*?)\):\s+(.*?)(?:\s+—\s*(.*))?$/i);
                        return {
                           trade: update.trade || (match ? match[1] : 'Unknown'),
                           status: update.status || (match ? match[2] : 'Unknown'),
                           description: update.detail || update.description || (match ? match[3] : ''),
                           actorName: update.actorName,
                           actorRole: update.actorRole,
                           progress: update.progress,
                           workStartTime: update.workStartTime || '',
                           workEndTime: update.workEndTime || '',
                           at: update.at,
                           images: update.images || []
                        };
                     })
                     .filter(u => String(u.trade).toLowerCase() === String(historyTrade).toLowerCase())
                     .sort((a, b) => new Date(b.at) - new Date(a.at))
                  }
               />
            </AccordionSection>

            {/* Developer : Nithin
                Date : 14-07-2026
                changes: Commented out the Materials Tab as it is not required */}
            {/* 7. Materials 
            <AccordionSection
               title="Materials"
               icon={<Package className="w-4 h-4" />}
               isOpen={openSections['materials']}
               onToggle={() => toggleSection('materials')}
               isCompleted={isMaterialRequestComplete}
            >
               <LeadExecutionMaterialsTab 
                  project={project} 
                  tasksInventoryLocked={tasksInventoryLocked}
                  viewerRole={(JSON.parse(localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user') || '{}')).role}
                  onUpdate={() => onUpdate(true)}
               />
            </AccordionSection> */}

            {/* 8. Documents and Designs */}
            <AccordionSection
               title="Customer & Project Documents"
               icon={<FileText className="w-4 h-4" />}
               isOpen={openSections['documentsAndDesigns']}
               onToggle={() => toggleSection('documentsAndDesigns')}
            >
               <div className="flex flex-col gap-4 bg-slate-50 p-3 rounded-b-xl border-x border-b border-slate-200">
                  <DocumentsTab project={project} onUpdate={onUpdate} />
                  {/* Commented out as requested
                  <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden mt-3">
                     <div className="bg-slate-100 px-3 py-2 border-b border-slate-200 font-bold text-xs text-slate-700">Designs</div>
                     <div className="flex flex-col bg-white border border-slate-100 rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-end gap-3">

                           <button
                              onClick={() => canUploadDocs && setActiveModal('uploadDesign')}
                              disabled={!canUploadDocs}
                              title={!canUploadDocs ? "Only specific roles can upload documents" : ""}
                              className={`flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors ${!canUploadDocs ? 'bg-slate-300 text-white cursor-not-allowed border-none' : 'border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50'
                                 }`}
                           >
                              <Plus className="w-3.5 h-3.5" />
                              Upload Design
                           </button>
                        </div>
                        <GalleryView project={project} category="designs" />
                     </div>
                  </div>
                  */}
               </div>
            </AccordionSection>

            {/* 9. Gallery */}
            <AccordionSection
               title="Site Photos & Gallery"
               icon={<Images className="w-4 h-4" />}
               isOpen={openSections['gallery']}
               onToggle={() => toggleSection('gallery')}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <GalleryTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

            {/* 10. Final Review */}
            <AccordionSection
               title="Final Review"
               icon={<ClipboardCheck className="w-4 h-4" />}
               isOpen={openSections['finalReview']}
               onToggle={() => toggleSection('finalReview')}
            >
               <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                  <FinalReviewTab project={project} onUpdate={onUpdate} />
               </div>
            </AccordionSection>

         </div>

         {/* Modals for Forms */}
         <Modal
            isOpen={activeModal === 'pm'}
            onClose={() => setActiveModal(null)}
            title="Assign Project Manager"
         >
            <ProjectManagerAssignmentForm
               project={project}
               onUpdate={onUpdate}
               onClose={() => setActiveModal(null)}
            />
         </Modal>

         <CreditPaymentForm
            isOpen={activeModal === 'payment'}
            onClose={() => setActiveModal(null)}
            projects={[project]}
            targetStatus="additional_payment"
            onConfirm={async (data) => {
               try {
                  const payload = {
                     amount: Number(data.amount),
                     paymentStage: data.stage,
                     paymentDate: data.paymentDate,
                     paymentMethod: data.method,
                     transactionId: data.transactionId,
                     bankName: data.bankName,
                     branch: data.branch,
                     upiPhone: data.upiPhone,
                     receiverName: data.receiverName,
                     receiverPhone: data.receiverPhone,
                     chequeNumber: data.chequeNumber,
                     chequeDate: data.chequeDate,
                     remarks: data.remarks,
                     relatedProjectId: project.leadId?._id || project.leadId || project._id,
                     relatedProjectCollection: 'VerveContact'
                  };

                  await api.post("/received-amounts", payload);

                  if (onUpdate) onUpdate(true); // Triggers a silent refetch of the project, updating side stats

                  // Also refresh local receivedAmounts list
                  const res = await api.get('/received-amounts');
                  const rawAmounts = res?.data?.data || [];
                  const projectAmounts = rawAmounts.filter(item =>
                     item.relatedProjectId?._id === project._id ||
                     item.relatedProjectId === project._id ||
                     item.projectId === project._id ||
                     item.relatedProjectId?.customId === project.projectId ||
                     item.relatedProjectId?._id === project.leadId?._id ||
                     item.relatedProjectId === project.leadId?._id
                  );
                  const formatted = projectAmounts.map(item => ({
                     _id: item._id,
                     receiptId: item.receiptId,
                     projectId: item.relatedProjectId?.customerCustomId || item.relatedProjectId?.projectId || item.relatedProjectId?.customId || "—",
                     clientName: item.relatedProjectId?.customerName || item.relatedProjectId?.firstName || "—",
                     projectName: item.relatedProjectId?.projectName || "—",
                     project: item.relatedProjectId,
                     paymentStage: item.paymentStage,
                     paymentDate: item.paymentDate,
                     paymentMode: item.paymentMethod,
                     amountReceived: item.amount,
                     balanceAmount: 0,
                     status: item.status,
                     transactionId: item.transactionId,
                     bankName: item.bankName,
                     branch: item.branch,
                     upiPhone: item.upiPhone,
                     receiverName: item.receiverName,
                     receiverPhone: item.receiverPhone,
                     chequeNumber: item.chequeNumber,
                     chequeDate: item.chequeDate,
                     remarks: item.remarks,
                     isFollowup: item.isFollowup,
                     followupReason: item.followupReason,
                     approxPaymentDate: item.approxPaymentDate,
                     followupDescription: item.followupDescription,
                     isBlinkingStopped: item.isBlinkingStopped
                  }));
                  setReceivedAmounts(formatted);

                  setActiveModal(null);
                  if (setToast) setToast({ message: 'Payment recorded successfully!', type: 'success' });
               } catch (err) {
                  console.error('Error recording payment:', err);
                  const msg = err.response?.data?.message || err.message || 'Failed to record payment.';
                  if (setToast) setToast({ message: msg, type: 'error' });
               }
            }}
         />

         <Modal
            isOpen={activeModal === 'team'}
            onClose={() => { setActiveModal(null); setEditingTeamMember(null); }}
            title="Assign Team Members"
            width="max-w-xl"
         >
            <LeadExecutionTeamAssignmentForm
               project={project}
               onUpdate={onUpdate}
               onClose={() => { setActiveModal(null); setEditingTeamMember(null); }}
               initialEditMember={editingTeamMember}
            />
         </Modal>

         <ScheduleTaskModal
            isOpen={activeModal === 'task' || activeModal === 'editTask'}
            onClose={() => {
               setActiveModal(null);
               setSelectedTask(null);
            }}
            project={project}
            eligibleMembers={eligibleFieldOfficers}
            taskToEdit={activeModal === 'editTask' ? selectedTask : null}
            onSuccess={() => {
               setActiveModal(null);
               setSelectedTask(null);
               if (onUpdate) onUpdate(true);
               if (refreshTasks) refreshTasks();
            }}
         />

         <ProjectMediaUploadModal
            isOpen={activeModal === 'uploadDocument' || activeModal === 'uploadDesign' || activeModal === 'uploadGallery'}
            onClose={() => setActiveModal(null)}
            project={project}
            onSuccess={(updated) => onUpdate(updated)}
            docType={activeModal === 'uploadDesign' ? 'design' : activeModal === 'uploadGallery' ? 'gallery' : 'document'}
         />

         <AddWorkUpdateModal
            isOpen={Boolean(workUpdateTask)}
            onClose={() => setWorkUpdateTask(null)}
            task={workUpdateTask}
            onSuccess={async () => {
               setWorkUpdateTask(null);
               if (onUpdate) onUpdate(true);
               if (refreshTasks) refreshTasks();
            }}
         />

         <LeadExecutionTradeWorkUpdateModal
            isOpen={Boolean(updateTrade)}
            onClose={() => setUpdateTrade(null)}
            project={project}
            trade={updateTrade}
            setToast={setToast}
            onSuccess={() => {
               setUpdateTrade(null);
               if (onUpdate) onUpdate();
            }}
         />

         {activeModal === 'taskDetail' && (
            <TaskDetailsModal
               isOpen={true}
               onClose={() => setActiveModal(null)}
               task={selectedTask}
            />
         )}

         <Modal
            isOpen={!!documentToApprove}
            onClose={() => setDocumentToApprove(null)}
            title="Approve Document"
         >
            <div className="p-4 space-y-4">
               <p className="text-sm text-slate-600">
                  Are you sure you want to approve the document: <strong>{documentToApprove?.originalName}</strong>?
               </p>
               <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                     type="button"
                     onClick={() => setDocumentToApprove(null)}
                     className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                  >
                     Cancel
                  </button>
                  <button
                     type="button"
                     onClick={async () => {
                        try {
                           const { data } = await api.post(`/verve-projects/${project._id}/documents/${documentToApprove._id}/approve`);
                           setToast({ message: 'Document approved successfully', type: 'success' });
                           if (data) onUpdate(data);
                           setDocumentToApprove(null);
                        } catch (error) {
                           setToast({ message: 'Failed to approve document', type: 'error' });
                        }
                     }}
                     className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors shadow-sm"
                  >
                     Approve
                  </button>
               </div>
            </div>
         </Modal>


      </div>
   );
};
